import React, {
  forwardRef,
  useState,
  useRef,
  useImperativeHandle,
  useCallback,
  useTransition,
  useMemo
} from "react";
import PropTypes from "prop-types";
import { Icon } from "@washingtonpost/wpds-icon";
import { getClasses } from "@washingtonpost/front-end-utils";
import "../../../../common/styles/animations.css";
import "./promo.css";
import { PromoState } from "../../../globals";
import {
  getBackgroundImage,
  getBackgroundImageCSS,
  getBackgroundVideo,
  getPromoDuration,
  getPromoTitle,
  getShouldRenderPromo
} from "./utils";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import { useAutoplayContext } from "../../contexts/AutoplayContext";
import { PlayerSize } from "../../hooks/usePlayerSize";
import {
  checkIfVideoEnded,
  checkIfVideoUpcoming,
  checkIfVideoTypeLive
} from "../../utils/liveVideoUtils";
import { PromoContainer, PromoVideo, PromoWrapper } from "./index.stitches";
import { PromoText } from "./components/PromoText";
import { PromoButton } from "./components/PromoButton";
import { useOrcaCommonContext } from "../../contexts/OrcaCommonContext";
import { useOrcaAdsContext } from "../../contexts/OrcaAdsContext";

// add a `toString` method
Icon.toString = () => ".wpds-icon";

export const Promo = forwardRef(
  (
    {
      onClick,
      promoState,
      setPromoState,
      config,
      promoEnabled: isPromoEnabled,
      aspectRatio,
      aspectMatch
    },
    ref
  ) => {
    const {
      alternateArt = null,
      isLivePreviewEnabled,
      placeholder,
      hidePromoVideo
    } = config;

    const { videoData, videoApiDataFailed: isVideoApiDataFailed } =
      useVideoDataContext();
    const { autoplayState } = useAutoplayContext();
    const { playerSize } = useOrcaCommonContext();
    const { shouldShowAds } = useOrcaAdsContext();

    const [backgroundVideoFailed, setBackgroundVideoFailed] = useState(false);
    const promoRef = useRef();

    const [, startTransition] = useTransition();

    const isVideoTypeLive = checkIfVideoTypeLive(videoData);
    const isUpcoming = checkIfVideoUpcoming(videoData);
    const isEnded = checkIfVideoEnded(videoData);

    const shouldRenderPromo = useMemo(
      () =>
        getShouldRenderPromo(
          autoplayState,
          shouldShowAds,
          videoData,
          config,
          isPromoEnabled,
          isVideoApiDataFailed
        ),
      [
        autoplayState,
        config,
        isPromoEnabled,
        isVideoApiDataFailed,
        shouldShowAds,
        videoData
      ]
    );
    const title = getPromoTitle(videoData);
    const backgroundImage = useMemo(
      () =>
        getBackgroundImage(
          videoData,
          placeholder,
          alternateArt,
          autoplayState?.isPlaythrough,
          aspectRatio
        ),
      [
        alternateArt,
        aspectRatio,
        autoplayState?.isPlaythrough,
        placeholder,
        videoData
      ]
    );

    const backgroundVideo = getBackgroundVideo(videoData);
    const duration = getPromoDuration(videoData);
    const hasVideo = backgroundVideo && !backgroundVideoFailed;
    const hideVideo = autoplayState?.isPlaythrough || hidePromoVideo;
    const renderPromoButton = isUpcoming === false && isEnded === false;
    const showBackgroundImage = !hasVideo && !isLivePreviewEnabled;

    const backgroundImageCSS = useMemo(
      () => getBackgroundImageCSS(backgroundImage, aspectMatch, aspectRatio),
      [aspectMatch, aspectRatio, backgroundImage]
    );

    useImperativeHandle(ref, () => ({
      playVideo() {
        setPromoState(PromoState.HIDDEN);
        onClick();
      }
    }));

    const handleOnClick = useCallback(
      event => {
        if (promoState !== PromoState.READY) return;
        startTransition(() => {
          event.preventDefault();
          event.stopPropagation();
          if (isVideoTypeLive && isUpcoming) return;
          onClick();
        });
      },
      [isUpcoming, isVideoTypeLive, onClick, promoState]
    );

    return (
      <>
        {/* Dont show promo if disabled or if autoplay tags exist. */}
        {shouldRenderPromo && (
          <PromoWrapper
            ref={promoRef}
            data-testid="orca-promo"
            css={{
              ...(showBackgroundImage && backgroundImageCSS)
            }}
            hidden={promoState === PromoState.HIDDEN}
            onClick={handleOnClick}
          >
            {hasVideo && !hideVideo && (
              <PromoVideo
                src={backgroundVideo}
                onError={() => setBackgroundVideoFailed(true)}
                onAbort={() => setBackgroundVideoFailed(true)}
                playsInline
                muted
                autoPlay
                loop
                crossOrigin="anonymous"
                controlsList="nodownload"
              />
            )}
            {playerSize && (
              <PromoContainer
                className={getClasses("", { [`${playerSize}`]: playerSize })}
              >
                {renderPromoButton && (
                  <PromoButton
                    {...{
                      promoState,
                      isVideoTypeLive,
                      isLivePreviewEnabled,
                      title,
                      duration
                    }}
                  />
                )}
                <PromoText
                  {...{
                    playerSize,
                    isEnded,
                    isUpcoming,
                    isVideoTypeLive,
                    title,
                    duration,
                    config
                  }}
                />
              </PromoContainer>
            )}
          </PromoWrapper>
        )}
      </>
    );
  }
);

Promo.propTypes = {
  /** Click handler (used by Orca) */
  onClick: PropTypes.func,
  promoState: PropTypes.string,
  setPromoState: PropTypes.func,
  config: PropTypes.object,
  autoplayState: PropTypes.object,
  promoEnabled: PropTypes.bool,
  shouldShowAds: PropTypes.bool,
  aspectRatio: PropTypes.number,
  playerSize: PropTypes.oneOf(Object.values(PlayerSize)),
  aspectMatch: PropTypes.bool
};

Promo.displayName = "Promo";
